import { useEffect, useState, type FormEvent } from 'react';

import { Modal } from '../ui';
import { api } from '../../lib/api';
import type { GroupView, SystemView } from './types';
import { errorText } from './types';

export function GroupDialog({
  open,
  onClose,
  onSaved,
  editing,
  systems,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editing: GroupView | null;
  systems: SystemView[];
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [members, setMembers] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setName(editing?.name ?? '');
    setDescription(editing?.description ?? '');
    setMembers(editing?.memberIds ?? []);
  }, [open, editing]);

  const toggle = (id: string) =>
    setMembers((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));

  const valid = name.trim().length >= 2 && members.length > 0;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const payload = { name: name.trim(), description, memberIds: members };
    try {
      if (editing) {
        await api(`/connectiq/firewall/groups/${editing.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
      } else {
        await api('/connectiq/firewall/groups', { method: 'POST', body: JSON.stringify(payload) });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? `Edit group ${editing.name}` : 'New group'}>
      <form className="ciq-form" onSubmit={submit}>
        <label className="ciq-field">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} placeholder="Admin path" />
        </label>
        <label className="ciq-field">
          <span>Description (optional)</span>
          <input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} />
        </label>
        <fieldset className="ciq-group">
          <legend>Systems in this group</legend>
          {systems.map((s) => (
            <label key={s.id} className="ciq-check">
              <input type="checkbox" checked={members.includes(s.id)} onChange={() => toggle(s.id)} />
              <span>
                <strong>{s.name}</strong>
                <span className="ciq-muted small mono"> {s.address ?? 'no address'}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <div className="ciq-actions">
          <button type="button" className="ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" disabled={busy || !valid}>
            {busy ? 'Saving…' : editing ? 'Save group' : 'Create group'}
          </button>
        </div>
        {error && <p className="form-error" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}
